/**
 * Fooocus preset bridge — mirrors `constants/image/fooocus_presets.py`.
 * The actual values come from shared_constants.json (kept in sync by
 * `python scripts/generate_shared_constants.py`). This file just provides
 * typed accessors and the subset we surface in the UI.
 */

import sharedConstants from './shared_constants.json';

const misc = sharedConstants.constants.misc as unknown as {
  FOOOCUS_PRESETS: Record<string, string[]>;
  FOOOCUS_PRESET_NAMES: string[];
  FOOOCUS_PRESET_LABELS: Record<string, string>;
};
const defaults = sharedConstants.constants.defaults as unknown as {
  FOOOCUS_DEFAULT_PRESET: string;
};
const limits = sharedConstants.constants.limits as {
  MAX_FOOOCUS_STYLES: number;
};

/** Preset name -> default_styles (base quality scaffolding). */
export const FOOOCUS_PRESETS: Readonly<Record<string, readonly string[]>> = misc.FOOOCUS_PRESETS;

/** Ordered list of preset names (same order as in the Python source). */
export const FOOOCUS_PRESET_NAMES: readonly string[] = misc.FOOOCUS_PRESET_NAMES;

/** Human-readable UI labels per preset. */
export const FOOOCUS_PRESET_LABELS: Readonly<Record<string, string>> = misc.FOOOCUS_PRESET_LABELS;

/** Preset used when the LLM/user does not specify one. */
export const FOOOCUS_DEFAULT_PRESET: string = defaults.FOOOCUS_DEFAULT_PRESET;

/** UI and backend cap for per-image style count. */
export const MAX_FOOOCUS_STYLES: number = limits.MAX_FOOOCUS_STYLES;

/**
 * User-facing presets. Skips `lcm`/`playground_v2.5`/`pony_v6` because those
 * target specific checkpoints and aren't useful as "click to set quality
 * scaffolding" shortcuts for the end user.
 */
export const FOOOCUS_UI_PRESETS: readonly string[] = ['default', 'sai', 'realistic', 'anime'];

export interface FooocusPresetOption {
  /** Preset name (e.g. "realistic"). */
  name: string;
  /** UI label (e.g. "Realistic / Photo"). */
  label: string;
  /** Base styles applied when the preset is selected. */
  styles: readonly string[];
}

/** Ordered list of preset options for the UI button row. */
export const FOOOCUS_UI_PRESET_OPTIONS: readonly FooocusPresetOption[] = FOOOCUS_UI_PRESETS.map(
  (name) => ({
    name,
    label: FOOOCUS_PRESET_LABELS[name] ?? name,
    styles: FOOOCUS_PRESETS[name] ?? [],
  })
);

/**
 * Check if the currently selected styles exactly match a preset's default_styles
 * (order-insensitive). Used by the UI to highlight the active preset button.
 */
export function isPresetActive(
  preset: FooocusPresetOption,
  selectedStyles: readonly string[]
): boolean {
  if (preset.styles.length !== selectedStyles.length) return false;
  const sortedPreset = [...preset.styles].sort();
  const sortedSelected = [...selectedStyles].sort();
  return sortedPreset.every((s, i) => s === sortedSelected[i]);
}
